import React, { useState, useEffect } from 'react';
import {
  Modal, Tree, message, Spin, Alert
} from 'antd';
import api from '../utils/api';

/**
 * 管理员权限分配组件
 */
const AdminPermissionModal = ({ visible, adminId, adminName, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [menuTree, setMenuTree] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);

  useEffect(() => {
    if (visible && adminId) {
      fetchData();
    }
  }, [visible, adminId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 获取所有菜单树
      const menuRes = await api.get('/admin/menus/tree', {
        params: { status: 1 }
      });

      // 获取管理员已有的菜单权限
      const permRes = await api.get(`/admin/menus/admin/${adminId}`);

      const menus = menuRes.data;
      const menuIds = permRes.data.menuIds;

      // 转换为Tree组件需要的格式
      const treeData = convertToTreeData(menus);
      setMenuTree(treeData);
      setCheckedKeys(menuIds);

      // 默认展开所有节点
      const allKeys = getAllKeys(menus);
      setExpandedKeys(allKeys);

    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 转换菜单数据为Tree组件格式
  const convertToTreeData = (menus) => {
    return menus.map(menu => ({
      title: `${menu.menu_name}${menu.permission ? ` (${menu.permission})` : ''}`,
      key: menu.id,
      children: menu.children ? convertToTreeData(menu.children) : undefined
    }));
  };

  // 获取所有菜单ID（用于展开）
  const getAllKeys = (menus) => {
    let keys = [];
    menus.forEach(menu => {
      keys.push(menu.id);
      if (menu.children && menu.children.length > 0) {
        keys = keys.concat(getAllKeys(menu.children));
      }
    });
    return keys;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post('/admin/menus/assign', {
        adminId,
        menuIds: checkedKeys
      });

      message.success('权限分配成功');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      message.error('权限分配失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheck = (checkedKeysValue) => {
    setCheckedKeys(checkedKeysValue);
  };

  const handleExpand = (expandedKeysValue) => {
    setExpandedKeys(expandedKeysValue);
  };

  const handleExpandAll = () => {
    const allKeys = getAllKeysFromTree(menuTree);
    setExpandedKeys(allKeys);
  };

  const handleCollapseAll = () => {
    setExpandedKeys([]);
  };

  const getAllKeysFromTree = (tree) => {
    let keys = [];
    tree.forEach(node => {
      keys.push(node.key);
      if (node.children && node.children.length > 0) {
        keys = keys.concat(getAllKeysFromTree(node.children));
      }
    });
    return keys;
  };

  return (
    <Modal
      title={`分配权限 - ${adminName}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      width={600}
      confirmLoading={submitting}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin tip="加载中..." />
        </div>
      ) : (
        <>
          <Alert
            message="提示"
            description="勾选菜单即可为管理员分配对应的访问权限。超级管理员默认拥有所有权限。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <div style={{ marginBottom: 8 }}>
            <a onClick={handleExpandAll} style={{ marginRight: 16 }}>展开全部</a>
            <a onClick={handleCollapseAll}>收起全部</a>
          </div>

          <div style={{ 
            border: '1px solid #d9d9d9', 
            borderRadius: 4, 
            padding: 16,
            maxHeight: 400,
            overflow: 'auto'
          }}>
            <Tree
              checkable
              checkedKeys={checkedKeys}
              expandedKeys={expandedKeys}
              onCheck={handleCheck}
              onExpand={handleExpand}
              treeData={menuTree}
            />
          </div>

          <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
            已选择 {checkedKeys.length} 个菜单权限
          </div>
        </>
      )}
    </Modal>
  );
};

export default AdminPermissionModal;
